/**
 * Hand-coded food-and-dining situational phrases (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0 (per repo data-layer policy).
 *
 * Convention: ALL variants of a `situationFamily` MUST live in the same
 * category file. Includes ladder marquee `order-coffee` at A2/B1/B2.
 */

import type { SituationCard } from '../../situations/types.js';

const foodDining: SituationCard[] = [
  {
    id: 'food-dining.table-for-two',
    situationId: 'food-dining.table-for-two',
    front: 'A table for two, please.',
    back: '二人席をお願いします。',
    category: 'food-dining',
    cefr: 'A1',
    translations: { ja: 'にせきをおねがいします。' },
    speechAct: 'request',
    speakers: 2,
    partner: { en: 'Right this way.', ja: 'こちらへどうぞ。' },
    formality: 'polite',
    channel: 'in-person',
    tags: ['service', 'host-stand']
  },
  {
    id: 'food-dining.menu-please',
    situationId: 'food-dining.menu-please',
    front: 'Could we see the menu, please?',
    back: 'メニューを見せてください。',
    category: 'food-dining',
    cefr: 'A2',
    translations: { ja: 'メニューをみせてください。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['service']
  },
  // Ladder marquee — ordering coffee at three CEFR registers
  {
    id: 'food-dining.order-coffee.a2',
    situationId: 'food-dining.order-coffee.a2',
    front: 'A small black coffee, please.',
    back: 'スモールサイズのブラックコーヒーをください。',
    category: 'food-dining',
    cefr: 'A2',
    translations: { ja: 'スモールサイズのブラックコーヒーをください。' },
    speechAct: 'request',
    situationFamily: 'order-coffee',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'food-dining.order-coffee.b1',
    situationId: 'food-dining.order-coffee.b1',
    front: "I'll take a flat white with oat milk — for here, thanks.",
    back: 'オートミルクのフラットホワイトを店内で。ありがとう。',
    category: 'food-dining',
    cefr: 'B1',
    translations: { ja: 'オートミルクのフラットホワイトをてんないで。ありがとう。' },
    speechAct: 'request',
    situationFamily: 'order-coffee',
    formality: 'casual',
    channel: 'in-person',
    pragmaticNote: {
      en: 'English uses casual hedges ("I\'ll take…", "thanks") even in service settings — Japanese ですます feels formal by comparison.',
      ja: '英語は店員さんに対してもくだけた表現（"I\'ll take…", "thanks"）を使います。ですます調と異なります。'
    }
  },
  {
    id: 'food-dining.order-coffee.b2',
    situationId: 'food-dining.order-coffee.b2',
    front: 'Could you make that with half-caf? And whatever\'s quickest — I\'m in a bit of a rush.',
    back: 'ハーフカフェインでお願いできますか？早くできるものがあればそれで——ちょっと急いでいて。',
    category: 'food-dining',
    cefr: 'B2',
    translations: { ja: 'ハーフカフェインでおねがいできますか？はやくできるものがあればそれで——ちょっといそいでいて。' },
    speechAct: 'request',
    situationFamily: 'order-coffee',
    formality: 'polite',
    channel: 'in-person',
    notes: {
      en: 'B2 hedging stack: "Could you…?" + "And whatever\'s quickest" + "a bit of a rush" — multiple softeners.',
      ja: 'B2は依頼に対して複数のクッション言葉を重ねます。'
    }
  },
  {
    id: 'food-dining.allergy',
    situationId: 'food-dining.allergy',
    front: "I'm allergic to peanuts. Does this dish contain any?",
    back: 'ピーナッツアレルギーがあります。これに入っていますか？',
    category: 'food-dining',
    cefr: 'B1',
    translations: { ja: 'ピーナッツアレルギーがあります。これにはいっていますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['safety', 'health']
  },
  {
    id: 'food-dining.check-please',
    situationId: 'food-dining.check-please',
    front: 'Could I have the check, please?',
    back: 'お会計をお願いします。',
    category: 'food-dining',
    cefr: 'A2',
    translations: { ja: 'おかいけいをおねがいします。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    tags: ['payment']
  },
  {
    id: 'food-dining.split-bill',
    situationId: 'food-dining.split-bill',
    front: 'Could we split the bill, please?',
    back: '会計を別々にできますか？',
    category: 'food-dining',
    cefr: 'B1',
    translations: { ja: 'かいけいをべつべつにできますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person',
    pragmaticNote: {
      en: 'Splitting the bill ("going Dutch") is much more common in casual Western dining than in Japan.',
      ja: '欧米のカジュアルな食事では割り勘がよくあります。日本ほど誰か一人がまとめて払う習慣は強くありません。'
    },
    tags: ['payment']
  },
  {
    id: 'food-dining.recommend',
    situationId: 'food-dining.recommend',
    front: 'What would you recommend?',
    back: 'おすすめは何ですか？',
    category: 'food-dining',
    cefr: 'A2',
    translations: { ja: 'おすすめはなんですか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'in-person'
  },
  {
    id: 'food-dining.compliment-meal',
    situationId: 'food-dining.compliment-meal',
    front: 'Everything was delicious — thank you.',
    back: 'すべておいしかったです。ありがとうございました。',
    category: 'food-dining',
    cefr: 'A1',
    translations: { ja: 'すべておいしかったです。ありがとうございました。' },
    speechAct: 'gratitude',
    formality: 'polite',
    channel: 'in-person'
  }
];

export default foodDining;
